import { Route, Routes, useLocation } from "react-router-dom";
import Layout from "../Layout/Layout.jsx";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import RestrictedRoute from "../RestrictedRoute.jsx";
import PrivateRoute from "../PrivateRoute.jsx";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsLoggedIn,
  selectIsRefreshing,
  selectLoading,
  selectSelectedServiceId,
  selectUser,
} from "../../redux/auth/selectors.js";
import { Toaster } from "react-hot-toast";
import { logOut, refreshUser } from "../../redux/auth/operations.js";
import firstPage from "../../utils/firstPage.js";
import css from "./App.module.css";
import Header from "../Header/Header.jsx";
import SideBar from "../SideBar/SideBar.jsx";
import LoaderSvg from "../Loader/LoaderSvg.jsx";
import {
  checkTariffs,
  getAllServices,
} from "../../redux/service/operations.js";
import {
  selectCheckTariffsLoading,
  selectTariffsInfo,
} from "../../redux/service/selectors.js";
import Modal from "../Modals/Modal/Modal.jsx";
import PaymentTopUpAccountModal from "../Modals/PaymentTopUpAccountModal/PaymentTopUpAccountModal.jsx";
import TariffNoticeModal from "../Modals/TariffNoticeModal/TariffNoticeModal.jsx";
import { getDaysLabel } from "../../utils/getDaysLabel.js";
import TariffExpiredWarningModal from "../Modals/TariffExpiredWarningModal/TariffExpiredWarningModal.jsx";
import ForbiddenPage from "../../pages/ForbiddenPage/ForbiddenPage.jsx";
import Support from "../Support/Support.jsx";
import { getChangeLogForCurrentVersion } from "../../redux/changeLog/operations.js";
import { getUnreadNotificationsCount } from "../../redux/notifications/operations.js";
import { createNotificationsSocket } from "../../services/notificationsSocket.js";
import { selectAccessToken } from "../../redux/auth/selectors.js";
import NotificationToast from "../NotificationToast/NotificationToast.jsx";

// const HomePage = lazy(() => import("../../pages/HomePage/HomePage.jsx"));
const LoginPage = lazy(() => import("../../pages/LoginPage/LoginPage.jsx"));
const RegistrationPage = lazy(
  () => import("../../pages/RegistrationPage/RegistrationPage.jsx")
);
const MainPage = lazy(() => import("../../pages/MainPage/MainPage.jsx"));
const VideoControlPage = lazy(
  () => import("../../pages/VideoControlPage/VideoControlPage.jsx")
);
const CRMPage = lazy(() => import("../../pages/CRMPage/CRMPage.jsx"));
const ConnectionsPage = lazy(
  () => import("../../pages/ConnectionsPage/ConnectionsPage.jsx")
);
const RecommendationsPage = lazy(
  () => import("../../pages/RecommendationsPage/RecommendationsPage.jsx")
);
// const AccountingPage = lazy(() =>
//   import("../../pages/AccountingPage/AccountingPage.jsx")
// );
const ClientsPage = lazy(
  () => import("../../pages/AccountingPage/ClientsPage/ClientsPage.jsx")
);
// const GeneralClientsListPage = lazy(() =>
//   import(
//     "../../pages/AccountingPage/ClientsPage/GeneralClientsListPage/GeneralClientsListPage.jsx"
//   )
// );
// const ClientsListInWork = lazy(() =>
//   import(
//     "../../pages/AccountingPage/ClientsPage/ClientsListInWork/ClientsListInWork.jsx"
//   )
// );
const InvoicesPage = lazy(
  () => import("../../pages/AccountingPage/InvoicesPage/InvoicesPage.jsx")
);
const GoodsAndServices = lazy(
  () =>
    import("../../pages/AccountingPage/InvoicesPage/GoodsAndServices/GoodsAndServices.jsx")
);
// const Services = lazy(() =>
//   import("../../pages/AccountingPage/InvoicesPage/Services/Services.jsx")
// );
const Funds = lazy(
  () => import("../../pages/AccountingPage/InvoicesPage/Funds/Funds.jsx")
);
const Equipment = lazy(
  () =>
    import("../../pages/AccountingPage/InvoicesPage/Equipment/Equipment.jsx")
);
// const ReportsPage = lazy(() =>
//   import("../../pages/ReportsPage/ReportsPage.jsx")
// );

const ReportsClientsPage = lazy(
  () =>
    import("../../pages/ReportsPage/ReportsClientsPage/ReportsClientsPage.jsx")
);
const DistributorsPage = lazy(
  () => import("../../pages/ReportsPage/DistributorsPage/DistributorsPage.jsx")
);
const BalancePage = lazy(
  () => import("../../pages/ReportsPage/BalancePage/BalancePage.jsx")
);
const WarehouseReportPage = lazy(
  () =>
    import("../../pages/ReportsPage/WarehouseReportPage/WarehouseReportPage.jsx")
);
const ProfitPage = lazy(
  () => import("../../pages/ReportsPage/ProfitPage/ProfitPage.jsx")
);
const WarehousePage = lazy(
  () => import("../../pages/WarehousePage/WarehousePage.jsx")
);
const StockPage = lazy(
  () => import("../../pages/WarehousePage/StockPage/StockPage.jsx")
);
const ReceivedsPage = lazy(
  () => import("../../pages/WarehousePage/ReceivedsPage/ReceivedsPage.jsx")
);
const ReturnsPage = lazy(
  () => import("../../pages/WarehousePage/ReturnsPage/ReturnsPage.jsx")
);
const TransfersPage = lazy(
  () => import("../../pages/WarehousePage/TransfersPage/TransfersPage.jsx")
);
const RevaluationPage = lazy(
  () => import("../../pages/WarehousePage/RevaluationPage/RevaluationPage.jsx")
);
const InventoryPage = lazy(
  () => import("../../pages/WarehousePage/InventoryPage/InventoryPage.jsx")
);
const WriteOffsPage = lazy(
  () => import("../../pages/WarehousePage/WriteOffsPage/WriteOffsPage.jsx")
);
const StaffPage = lazy(() => import("../../pages/StaffPage/StaffPage.jsx"));
const EmployeesPage = lazy(
  () => import("../../pages/StaffPage/EmployeesPage/EmployeesPage.jsx")
);
const TimesheetPage = lazy(
  () => import("../../pages/StaffPage/TimesheetPage/TimesheetPage.jsx")
);
const SalaryPage = lazy(
  () => import("../../pages/StaffPage/SalaryPage/SalaryPage.jsx")
);

const SettingsPage = lazy(
  () => import("../../pages/SettingsPage/SettingsPage.jsx")
);
const BonusesPage = lazy(
  () => import("../../pages/BonusesPage/BonusesPage.jsx")
);
const RatingPage = lazy(() => import("../../pages/RatingPage/RatingPage.jsx"));
const ProficiencyPage = lazy(
  () => import("../../pages/ProficiencyPage/ProficiencyPage.jsx")
);
const NotFoundPage = lazy(
  () => import("../../pages/NotFoundPage/NotFoundPage.jsx")
);
const ChangePasswordWithEmailPage = lazy(
  () =>
    import("../../pages/ChangePasswordWithEmailPage/ChangePasswordWithEmailPage.jsx")
);
const ValidateEmailPage = lazy(
  () => import("../../pages/ValidateEmailPage/ValidateEmailPage.jsx")
);

const HelpPage = lazy(() => import("../../pages/HelpPage/HelpPage.jsx"));

const RegistrationFormPage = lazy(
  () => import("../../pages/RegistrationFormPage/RegistrationFormPage.jsx")
);

const ThanksForRegistrationPage = lazy(
  () =>
    import("../../pages/ThanksForRegistrationPage/ThanksForRegistrationPage.jsx")
);

export default function App() {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectLoading);
  const isCheckTariffsLoading = useSelector(selectCheckTariffsLoading);
  const isRefreshing = useSelector(selectIsRefreshing);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const tariffsInfo = useSelector(selectTariffsInfo);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [tariffsModalOpen, setTariffsModalOpen] = useState(false);
  // Попередження, що термін дії тарифу скоро закінчується
  const [tariffWarningOpen, setTariffWarningOpen] = useState(false);
  const [expiredTariff, setExpiredTariff] = useState(false);
  const [remainingDaysTillDelete, setRemainingDaysTillDelete] = useState(false);
  // Попередження про вихід, якщо тариф не продовжено
  const [logoutWarningOpen, setLogoutWarningOpen] = useState(false);
  const selectedServiceIdInState = useSelector(selectSelectedServiceId);

  const location = useLocation();

  // console.log("tariffsInfo", tariffsInfo);

  const isResetPasswordPage = location.pathname === "/reset-password";
  const isLoginPage = location.pathname === "/login";
  const isRegistrationPage = location.pathname === "/register";

  useEffect(() => {
    if (!isLoggedIn || isLoginPage) return;

    dispatch(getUnreadNotificationsCount());
  }, [dispatch, isLoggedIn, isLoginPage, selectedServiceIdInState]);

  // 1. Перевірка при навігації
  useEffect(() => {
    if (!isLoggedIn || isLoginPage) return;
    dispatch(checkTariffs());
  }, [location.pathname, isLoggedIn]);

  // // 2. Перевірка за таймером (поки на одній сторінці)
  // useEffect(() => {
  //   if (!isLoggedIn || isLoginPage) return;

  //   const interval = setInterval(
  //     () => {
  //       dispatch(checkTariffs());
  //     },
  //     5 * 60 * 1000
  //   );

  //   return () => clearInterval(interval);
  // }, [isLoggedIn, isLoginPage]);

  useEffect(() => {
    const now = new Date();
    const paidUntil = new Date(tariffsInfo?.paid_until);

    const twentyOneDaysFromPaidUntil = new Date(paidUntil);
    twentyOneDaysFromPaidUntil.setDate(
      twentyOneDaysFromPaidUntil.getDate() + 21
    );

    // Кількість календарних днів до завершення терміну
    const remainingDays = Math.max(
      0,
      Math.ceil(
        (Date.UTC(
          twentyOneDaysFromPaidUntil.getFullYear(),
          twentyOneDaysFromPaidUntil.getMonth(),
          twentyOneDaysFromPaidUntil.getDate()
        ) -
          Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) /
          (1000 * 60 * 60 * 24)
      )
    );

    if (isLoggedIn && (now >= paidUntil || tariffsInfo?.status === "expired")) {
      setExpiredTariff(true);
      setRemainingDaysTillDelete(remainingDays);

      // Тариф прострочений: показуємо вікно, що блокує роботу з додатком
      setLogoutWarningOpen(true);
    } else if (
      tariffsInfo?.days_remaining <= 5 &&
      tariffsInfo?.status !== "expired"
    ) {
      // показуємо попередження лише один раз за сесію
      const warned = sessionStorage.getItem("tariffWarningShown");
      if (warned) return;

      sessionStorage.setItem("tariffWarningShown", "true");

      setTariffWarningOpen(true);
    }
  }, [tariffsInfo]);

  useEffect(() => {
    const init = async () => {
      try {
        await dispatch(refreshUser()).unwrap();
      } catch (e) {
        // це нормально якщо токен помер
      }

      !isLoginPage && !isRegistrationPage && dispatch(getAllServices());
      setIsAuthChecked(true);
    };

    init();
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn || isLoginPage) return;

    dispatch(getChangeLogForCurrentVersion());
  }, [dispatch, isLoggedIn, isLoginPage]);

  const handleCloseTariffsModal = () => {
    if (expiredTariff) {
      setLogoutWarningOpen(true);
      return;
    }
    setTariffsModalOpen(false);
  };

  const handleLogoutBecauseOfExpiredTariff = () => {
    setLogoutWarningOpen(false);
    dispatch(logOut());
    setTariffsModalOpen(false);
  };

  const accessToken = useSelector(selectAccessToken);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn || !accessToken) return;
    socketRef.current = createNotificationsSocket(accessToken, dispatch);
    return () => socketRef.current?.close();
  }, [isLoggedIn, accessToken, dispatch]);

  const userData = useSelector(selectUser);
  const renderPage = firstPage(userData);

  return (
    <Layout>
      <div className={css.wrapper}>
        {!isResetPasswordPage && <Header />}
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            style: {
              textAlign: "center",
              fontSize: "clamp(16px, min(1.297vw, 2.222vh), 24px)",
              padding:
                "clamp(12px, min(1.481vh, 0.864vw), 16px) clamp(16px, min(1.297vw, 2.222vh), 24px)",
              minWidth: "clamp(207px, min(16.216vw, 27.778vh), 300px)",
            },
          }}
        />
        <div className={css.pagesContent}>
          {!isResetPasswordPage && <SideBar />}

          {isLoading ||
          isCheckTariffsLoading ||
          isRefreshing ||
          !isAuthChecked ? (
            <LoaderSvg />
          ) : (
            <Suspense fallback={<LoaderSvg />}>
              <div className={css.routeContainer}>
                {tariffsModalOpen && (
                  <Modal
                    isOpen={tariffsModalOpen}
                    onClose={() => {
                      expiredTariff && dispatch(logOut());
                      setTariffsModalOpen(false);
                    }}
                  >
                    <PaymentTopUpAccountModal
                      onClose={handleCloseTariffsModal}
                    />
                  </Modal>
                )}
                {tariffWarningOpen && (
                  <Modal
                    isOpen={tariffWarningOpen}
                    onClose={() => setTariffWarningOpen(false)}
                  >
                    <TariffNoticeModal
                      title="Термін дії тарифного плану"
                      description={`закінчується через ${tariffsInfo?.days_remaining} ${getDaysLabel(tariffsInfo?.days_remaining)}. Будь ласка, продовжіть тариф, щоб уникнути перерви в користуванні додатком.`}
                      actionLabel="Продовжити тарифний план"
                      onAction={() => {
                        setTariffWarningOpen(false);
                        setTariffsModalOpen(true);
                      }}
                      onClose={() => setTariffWarningOpen(false)}
                    />
                  </Modal>
                )}
                {logoutWarningOpen && (
                  <Modal
                    isOpen={logoutWarningOpen}
                    onClose={handleLogoutBecauseOfExpiredTariff}
                    allowOverlayClickClose={false}
                    shouldCloseOnEsc={false}
                  >
                    <TariffExpiredWarningModal
                      remainingDaysTillDelete={remainingDaysTillDelete}
                      onContinue={() => {
                        setLogoutWarningOpen(false);
                        setTariffsModalOpen(true);
                      }}
                      onClose={handleLogoutBecauseOfExpiredTariff}
                    />
                  </Modal>
                )}
                {isLoggedIn && <Support />}
                {isLoggedIn && <NotificationToast />}
                <Routes>
                  <Route
                    path="/"
                    // element={
                    //   isLoggedIn ? (
                    //     <Navigate to={renderPage} replace />
                    //   ) : (
                    //     <HomePage />
                    //   )
                    // }
                    element={
                      <RestrictedRoute
                        redirectTo={renderPage}
                        component={<LoginPage />}
                      />
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <RestrictedRoute
                        redirectTo={renderPage}
                        component={<LoginPage />}
                      />
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <RestrictedRoute
                        redirectTo={renderPage}
                        component={<RegistrationPage />}
                      />
                    }
                  />
                  <Route
                    path="/registration-form"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<RegistrationFormPage />}
                      />
                    }
                  />
                  <Route
                    path="/thanks-for-registration"
                    element={<ThanksForRegistrationPage />}
                  />
                  {/* <Route
                    path="/main"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/main"
                        component={<MainPage />}
                      />
                    }
                  /> */}
                  <Route
                    path="/video-control"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/video-control"
                        component={<VideoControlPage />}
                      />
                    }
                  />
                  <Route
                    path="/crm"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/crm"
                        component={<CRMPage />}
                      />
                    }
                  />
                  <Route
                    path="/connections"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/connections"
                        component={<ConnectionsPage />}
                      />
                    }
                  />
                  <Route
                    path="/recommendations"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/recommendations"
                        component={<RecommendationsPage />}
                      />
                    }
                  />
                  {/* <Route
                    path="/accounting"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<AccountingPage />}
                      />
                    }
                  /> */}
                  <Route
                    path="/accounting/clients"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/accounting/clients"
                        component={<ClientsPage />}
                      />
                    }
                  />
                  {/* <Route
                    path="/accounting/clients/clients-list-general"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<GeneralClientsListPage />}
                      />
                    }
                  /> */}
                  {/* <Route
                    path="/accounting/clients/clients-list-in-work"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<ClientsListInWork />}
                      />
                    }
                  /> */}
                  <Route
                    path="/accounting/documents"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<InvoicesPage />}
                      />
                    }
                  >
                    <Route
                      path="goods-and-services"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/accounting/documents/goods-and-services"
                          component={<GoodsAndServices />}
                        />
                      }
                    />
                    {/* <Route path="services" element={<Services />} /> */}
                    <Route
                      path="funds"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/accounting/documents/funds"
                          component={<Funds />}
                        />
                      }
                    />
                    <Route
                      path="equipment"
                      accessPath="/accounting/documents/equipment"
                      element={<Equipment />}
                    />
                  </Route>
                  {/* <Route
                    path="/reports"
                    element={
                      <PrivateRoute
                      redirectTo="/login"
                      component={<ReportsPage />}
                      />
                      }
                      /> */}
                  <Route
                    path="/reports/clients"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/reports/clients"
                        component={<ReportsClientsPage />}
                      />
                    }
                  />
                  <Route
                    path="/reports/distributors"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/reports/distributors"
                        component={<DistributorsPage />}
                      />
                    }
                  />
                  <Route
                    path="/reports/balance"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/reports/balance"
                        component={<BalancePage />}
                      />
                    }
                  />
                  <Route
                    path="/reports/warehouse"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/reports/warehouse"
                        component={<WarehouseReportPage />}
                      />
                    }
                  />
                  <Route
                    path="/reports/profit"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/reports/profit"
                        component={<ProfitPage />}
                      />
                    }
                  />
                  <Route
                    path="/staff"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/staff"
                        component={<StaffPage />}
                      />
                    }
                  >
                    <Route
                      path="employees"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/staff/employees"
                          component={<EmployeesPage />}
                        />
                      }
                    />
                    <Route
                      path="timesheet"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/staff/timesheet"
                          component={<TimesheetPage />}
                        />
                      }
                    />
                    <Route
                      path="salary"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/staff/salary"
                          component={<SalaryPage />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="/warehouse"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/warehouse"
                        component={<WarehousePage />}
                      />
                    }
                  >
                    <Route
                      path="stock"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/stock"
                          component={<StockPage />}
                        />
                      }
                    />
                    <Route
                      path="receiveds"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/receiveds"
                          component={<ReceivedsPage />}
                        />
                      }
                    />
                    <Route
                      path="returns"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/returns"
                          component={<ReturnsPage />}
                        />
                      }
                    />
                    <Route
                      path="transfers"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/transfers"
                          component={<TransfersPage />}
                        />
                      }
                    />
                    <Route
                      path="revaluation"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/revaluation"
                          component={<RevaluationPage />}
                        />
                      }
                    />
                    <Route
                      path="inventory"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/inventory"
                          component={<InventoryPage />}
                        />
                      }
                    />
                    <Route
                      path="write-offs"
                      element={
                        <PrivateRoute
                          redirectTo="/login"
                          accessPath="/warehouse/write-offs"
                          component={<WriteOffsPage />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/settings"
                        component={<SettingsPage />}
                      />
                    }
                  />
                  <Route
                    path="/bonuses"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/bonuses"
                        component={<BonusesPage />}
                      />
                    }
                  />
                  <Route
                    path="/rating"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/rating"
                        component={<RatingPage />}
                      />
                    }
                  />
                  <Route
                    path="/proficiency"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        accessPath="/proficiency"
                        component={<ProficiencyPage />}
                      />
                    }
                  />
                  <Route
                    path="/validate-email/:api_key"
                    element={
                      <RestrictedRoute
                        redirectTo="/video-control"
                        component={<ValidateEmailPage />}
                      />
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={<ChangePasswordWithEmailPage />}
                  />
                  <Route
                    path="/help"
                    element={
                      <PrivateRoute
                        redirectTo="/login"
                        component={<HelpPage />}
                      />
                    }
                  />
                  <Route path="/forbidden" element={<ForbiddenPage />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </div>
            </Suspense>
          )}
        </div>
      </div>
    </Layout>
  );
}
